/**
 * WeekGrid — the working schedule for one job, one week.
 *
 * Rows are post x shift window, columns are the seven days. A stable eight-post
 * site is ~110 cells; a 40-post site running day, swing and night is ~120 rows
 * and ~840 cells, which is why:
 *
 *  - a cell is a plain button, and the officer picker is mounted ONCE, against
 *    whichever cell is open. 840 mounted pickers is what makes a grid feel
 *    slower than the spreadsheet it is replacing;
 *  - posts group by site and collapse, with hours and unfilled counts on the
 *    group header, so a 40-post week is readable collapsed and you open only
 *    the zone you are working on;
 *  - the day header row and the post column both pin, because at 120 rows a
 *    cell with no visible row or column label is unusable;
 *  - filters cut the rows rather than the data: unfilled only, over 40 only,
 *    armed only, one day only. The week stays one fetch and one model, so
 *    moving around and pasting never waits on the network.
 *
 * No drag and drop, by decision. Every bulk action - fill a row, copy and
 * paste across a range, duplicate for a second officer, change hours type or
 * billable, move somewhere else - is a keyboard shortcut AND a menu item, so
 * it works on a touch screen and for anyone who does not use a mouse.
 *
 * Rates: a pay rate appears on a shift row only for a viewer the server sent
 * one to, and the same is true of a bill rate. There is no client-side
 * decision here - a field that reaches the browser has already leaked - so
 * this component renders what it was given and never asks who is looking.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { OfficerPicker } from './OfficerPicker';
import { AvailableHoursSheet } from './AvailableHoursSheet';
import { ShiftBlockedDialog } from './ShiftBlockedDialog';
import { TimesDialog, MoveDialog, HoursTypeDialog, OpenPostDialog } from './GridDialogs';
import {
  AlertTriangle, CalendarPlus, ChevronDown, ChevronRight, Clipboard, Clock, Copy,
  MoreHorizontal, Moon, MoveRight, PartyPopper, Trash2, UserPlus, UserX, Users,
} from 'lucide-react';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const hhmm = (v) => (v || '').slice(0, 5);
const money = (tt) => (tt == null ? null : `$${(tt / 10000).toFixed(2)}`);
const windowKey = (s) => `${hhmm(s.planned_time_in || s.time_in)}-${hhmm(s.planned_time_out || s.time_out)}${s.next_day ? '+' : ''}`;

// The picker is a fixed-position panel roughly this tall (a 288px list plus the
// search box and the two footer actions). Anchoring it blindly below the cell
// put it off the bottom of the window for the lower posts on a 800px-tall
// laptop, so filling post 8 meant scrolling a popover that was already cut
// off. It flips above the cell when there is no room below, and is clamped to
// the viewport either way.
const PICKER_HEIGHT = 384;
const pickerHeight = () => Math.min(window.innerHeight * 0.85, 460);
const pickerTop = (rect) => {
  const H = pickerHeight();
  const below = rect.bottom + 6;
  if (below + H <= window.innerHeight - 8) return below;
  const above = rect.top - H - 6;
  if (above >= 8) return above;
  return Math.max(8, window.innerHeight - H - 8);
};

/** One officer's cell. Deliberately a button: no per-cell picker, no per-cell state. */
function Cell({ cellKey, shifts, selected, focused, names, showPay, showBill, canEdit,
                onOpen, onMenu, onHoliday, onSelect, cellRef }) {
  const primary = shifts[0];
  const extra = shifts.length - 1;
  const open = primary && (primary.is_open_post || !primary.employee_id);
  const name = primary?.employee_id
    ? (names.get(primary.employee_id) || 'Officer')
    : null;
  const suggested = primary?.holiday_suggestion_state === 'suggested';

  return (
    <td className={`p-0 align-top border-b border-line-subtle border-r last:border-r-0
                    ${selected ? 'bg-primary/10' : ''}`}
      data-testid={`grid-cell-${cellKey}`}>
      <div className="relative group">
        <button ref={cellRef} type="button"
          data-testid={`grid-cell-button-${cellKey}`}
          aria-label={primary ? `${name || 'Open post'} ${hhmm(primary.time_in)} to ${hhmm(primary.time_out)}` : 'Empty'}
          onClick={(e) => (e.shiftKey || e.metaKey || e.ctrlKey ? onSelect(e) : onOpen())}
          className={`w-full min-h-[3.25rem] text-left px-2 py-1.5 transition-colors
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                      ${focused ? 'ring-2 ring-primary ring-inset' : ''}
                      ${primary ? 'hover:bg-surface-sunken' : 'hover:bg-surface-sunken/60'}`}>
          {!primary ? (
            <span className="text-xs text-fg-muted/50">—</span>
          ) : (
            <>
              <span className={`block text-xs truncate ${open ? 'text-warning' : 'text-fg'}`}>
                {open ? 'Open' : name}
              </span>
              <span className="block text-[11px] text-fg-muted tabular-nums">
                {hhmm(primary.time_in)}–{hhmm(primary.time_out)}
                {primary.next_day && <Moon className="inline h-2.5 w-2.5 ml-0.5 -mt-0.5" />}
                {primary.hours_calculated != null && ` · ${primary.hours_calculated}h`}
              </span>
              <span className="flex flex-wrap items-center gap-1 mt-0.5">
                {primary.overtime_hours > 0 && (
                  <Badge variant="outline" data-testid={`cell-ot-${cellKey}`}
                    className="text-[10px] py-0 px-1 border-warning-border text-warning">
                    OT {primary.overtime_hours}h
                  </Badge>
                )}
                {primary.qualification_status && primary.qualification_status !== 'ok' && (
                  <AlertTriangle className="h-3 w-3 text-warning"
                    data-testid={`cell-unqualified-${cellKey}`} />
                )}
                {primary.call_off && (
                  <Badge variant="outline" className="text-[10px] py-0 px-1"
                    data-testid={`cell-calloff-${cellKey}`}>
                    {primary.call_off.reason}
                  </Badge>
                )}
                {primary.non_billable && (
                  <Badge variant="outline" className="text-[10px] py-0 px-1">NB</Badge>
                )}
                {primary.is_holiday && (
                  <PartyPopper className="h-3 w-3 text-fg-muted"
                    data-testid={`cell-holiday-${cellKey}`} />
                )}
                {showPay && primary.pay_rate_ten_thousandths != null && (
                  <span className="text-[10px] text-fg-muted tabular-nums"
                    data-testid={`cell-pay-${cellKey}`}>
                    {money(primary.pay_rate_ten_thousandths)}/h
                  </span>
                )}
                {showPay && primary.pay_rate_missing && (
                  <span className="text-[10px] text-warning" data-testid={`cell-pay-missing-${cellKey}`}>
                    no rate on file
                  </span>
                )}
                {showBill && primary.bill_rate_ten_thousandths != null && (
                  <span className="text-[10px] text-fg-muted tabular-nums"
                    data-testid={`cell-bill-${cellKey}`}>
                    bills {money(primary.bill_rate_ten_thousandths)}/h
                  </span>
                )}
                {showBill && primary.bill_rate_status && primary.bill_rate_status !== 'ok' && (
                  <span className="text-[10px] text-warning" data-testid={`cell-bill-issue-${cellKey}`}>
                    {primary.bill_rate_status === 'no_rate_line' ? 'no rate line'
                      : primary.bill_rate_status === 'unpriced' ? 'unpriced'
                      : primary.bill_rate_status === 'ambiguous' ? 'rate card overlaps' : ''}
                  </span>
                )}
                {extra > 0 && (
                  <Badge variant="outline" className="text-[10px] py-0 px-1"
                    data-testid={`cell-extra-${cellKey}`}>
                    +{extra}
                  </Badge>
                )}
              </span>
            </>
          )}
        </button>

        {suggested && canEdit && (
          <div className="px-2 pb-1.5 flex items-center gap-1" data-testid={`holiday-chip-${cellKey}`}>
            <span className="text-[10px] text-fg-muted">Holiday?</span>
            <button type="button" onClick={() => onHoliday(primary, 'accept')}
              data-testid={`holiday-accept-${cellKey}`}
              className="text-[10px] px-1.5 py-0.5 rounded-full border border-line
                         hover:border-primary text-fg">Yes</button>
            <button type="button" onClick={() => onHoliday(primary, 'decline')}
              data-testid={`holiday-decline-${cellKey}`}
              className="text-[10px] px-1.5 py-0.5 rounded-full border border-line
                         hover:border-primary text-fg-muted">No</button>
          </div>
        )}

        {canEdit && (
          <button type="button" onClick={(e) => onMenu(e)} aria-label="Cell actions"
            data-testid={`grid-cell-menu-${cellKey}`}
            className="absolute top-1 right-1 p-0.5 rounded text-fg-muted/70
                       opacity-60 md:opacity-0 group-hover:opacity-100 focus:opacity-100
                       hover:text-fg hover:bg-surface">
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </td>
  );
}

export function WeekGrid({
  weekId, weekStart, job, posts, dates, shifts, totals, employees, hoursTypes, callOffReasons,
  canEdit, showPay, showBill, blocked, onCommit, onResolveBlocked, onCancelBlocked,
  onHoliday, onOpenOfficer,
}) {
  const [collapsed, setCollapsed] = useState({});
  const [filter, setFilter] = useState('none');   // none | unfilled | overtime | armed
  const [dayFocus, setDayFocus] = useState('');
  const [selection, setSelection] = useState([]); // cellKeys
  const [focusKey, setFocusKey] = useState(null);
  const [picker, setPicker] = useState(null);     // { cell, anchor, rowFill }
  const [availOpen, setAvailOpen] = useState(false);
  const [clipboard, setClipboard] = useState(null);
  const [localBlock, setLocalBlock] = useState(null);
  const [dialog, setDialog] = useState(null);     // { kind, scope }
  const cellRefs = useRef(new Map());
  const gridRef = useRef(null);

  const names = useMemo(() => {
    const m = new Map();
    (employees || []).forEach((e) => m.set(
      e.id, `${e.last_name || ''}${e.first_name ? `, ${e.first_name}` : ''}`.trim() || e.employee_number));
    return m;
  }, [employees]);

  const overHours = useMemo(() => {
    const m = new Map();
    (totals?.officers || []).forEach((o) => m.set(o.employee_id, o));
    return m;
  }, [totals]);

  const postById = useMemo(() => {
    const m = new Map();
    (posts || []).forEach((p) => m.set(p.id, p));
    return m;
  }, [posts]);

  // ── rows: post x window, built from the week's own shifts ────────────────
  const rows = useMemo(() => {
    const byPost = new Map();
    (posts || []).forEach((p) => byPost.set(p.id, new Map()));
    (shifts || []).forEach((s) => {
      if (!byPost.has(s.job_post_id)) byPost.set(s.job_post_id, new Map());
      const wk = windowKey(s);
      const bucket = byPost.get(s.job_post_id);
      if (!bucket.has(wk)) {
        bucket.set(wk, {
          key: `${s.job_post_id}|${wk}`,
          postId: s.job_post_id,
          timeIn: hhmm(s.planned_time_in || s.time_in),
          timeOut: hhmm(s.planned_time_out || s.time_out),
          nextDay: Boolean(s.next_day),
          hoursTypeId: s.hours_type_id,
          billingItemId: s.billing_item_id,
          cells: {},
        });
      }
      const row = bucket.get(wk);
      (row.cells[s.shift_date] = row.cells[s.shift_date] || []).push(s);
    });
    const out = [];
    Array.from(byPost.entries()).forEach(([postId, bucket]) => {
      const post = postById.get(postId);
      const list = Array.from(bucket.values());
      if (!list.length) {
        out.push({
          key: `${postId}|empty`, postId, timeIn: '', timeOut: '', nextDay: false,
          hoursTypeId: post?.hours_type_id, billingItemId: post?.billing_item_id,
          cells: {}, post,
        });
        return;
      }
      list.sort((a, b) => (a.timeIn || '').localeCompare(b.timeIn || ''));
      list.forEach((r) => out.push({ ...r, post }));
    });
    out.sort((a, b) => (a.post?.post_name || '').localeCompare(b.post?.post_name || '')
      || (a.timeIn || '').localeCompare(b.timeIn || ''));
    return out;
  }, [posts, shifts, postById]);

  const groups = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const key = r.post?.site_id || 'site';
      if (!m.has(key)) m.set(key, { key, label: r.post?.site_name || job?.job_description || 'Posts', rows: [] });
      m.get(key).rows.push(r);
    });
    return Array.from(m.values());
  }, [rows, job]);

  const rowMatches = useCallback((row) => {
    const cells = Object.values(row.cells).flat();
    if (filter === 'unfilled') return cells.some((s) => s.is_open_post || !s.employee_id);
    if (filter === 'overtime') return cells.some((s) => s.overtime_hours > 0
      || (s.employee_id && overHours.get(s.employee_id)?.over_threshold));
    if (filter === 'armed') return cells.some((s) => s.qualification_status && s.qualification_status !== 'ok')
      || (row.post?.required_qualifications || []).some((q) => /armed/i.test(q) && !/unarmed/i.test(q));
    return true;
  }, [filter, overHours]);

  const shownDates = useMemo(() => (dayFocus ? [dayFocus] : dates), [dayFocus, dates]);

  // ── changes ──────────────────────────────────────────────────────────────
  const scaffold = (row, date) => ({
    job_post_id: row.postId,
    shift_date: date,
    time_in: row.timeIn || undefined,
    time_out: row.timeOut || undefined,
    next_day: row.nextDay,
    hours_type_id: row.hoursTypeId || undefined,
    billing_item_id: row.billingItemId || undefined,
  });

  const changeFor = (row, date, shift, fields, extra = {}) => ({
    key: shift?.id || `new:${row.key}|${date}`,
    change: {
      op: 'upsert',
      id: shift?.id || null,
      fields: shift ? fields : { ...scaffold(row, date), ...fields },
      ...extra,
    },
  });

  const cellsFor = (key) => {
    const [rowKey, date] = key.split('||');
    const row = rows.find((r) => r.key === rowKey);
    return { row, date, shifts: row?.cells[date] || [] };
  };

  const assign = (targets, employeeId, extra = {}) => {
    const items = targets.map(({ row, date, shifts: cellShifts }) =>
      changeFor(row, date, cellShifts[0], { employee_id: employeeId, is_open_post: false }, extra));
    if (items.length) onCommit(items);
  };

  const openPicker = (row, date, anchorEl, rowFill = false) => {
    if (!canEdit) return;
    const cellShifts = row.cells[date] || [];
    const s = cellShifts[0];
    const rect = anchorEl?.getBoundingClientRect();
    setPicker({
      rowFill,
      cell: {
        postId: row.postId,
        postName: row.post?.post_name || 'Post',
        date,
        timeIn: s?.time_in || row.timeIn,
        timeOut: s?.time_out || row.timeOut,
        nextDay: s?.next_day ?? row.nextDay,
        shiftId: s?.id,
        rowKey: row.key,
      },
      anchor: rect ? { top: pickerTop(rect), left: Math.max(8, Math.min(rect.left, window.innerWidth - 340)) } : null,
    });
  };

  const onPick = (officer) => {
    const { cell, rowFill } = picker;
    const row = rows.find((r) => r.key === cell.rowKey);
    const targets = rowFill
      ? dates.filter((d) => (row.cells[d] || []).length).map((d) => ({ row, date: d, shifts: row.cells[d] }))
      : [{ row, date: cell.date, shifts: row.cells[cell.date] || [] }];
    setPicker(null);
    chooseFor(officer, targets);
  };

  /** One assignment path, whatever opened it: a cell, a row fill, or the
   *  available-hours panel. So the same pop-ups always ask. */
  const chooseFor = (officer, targets) => {
    // Double-booking is named before it is saved, not discovered afterwards.
    if (officer.conflict_status === 'double_booked') {
      setLocalBlock({
        code: 'double_booked',
        conflicts: officer.conflicts,
        message: `${officer.display_last_first} is already scheduled at that time. `
          + 'You can still schedule them here.',
        apply: () => assign(targets, officer.employee_id),
      });
      return;
    }
    assign(targets, officer.employee_id);
  };

  const focusedCell = () => {
    if (!focusKey) return null;
    const { row, date, shifts: cellShifts } = cellsFor(focusKey);
    if (!row) return null;
    const s = cellShifts[0];
    return {
      postId: row.postId, postName: row.post?.post_name, date,
      timeIn: s?.time_in || row.timeIn, timeOut: s?.time_out || row.timeOut,
      nextDay: s?.next_day ?? row.nextDay, rowKey: row.key,
    };
  };

  const applyToSelection = (fields) => {
    const targets = (selection.length ? selection : focusKey ? [focusKey] : []).map(cellsFor);
    const items = targets.filter((t) => t.row).flatMap(({ row, date, shifts: cellShifts }) => {
      if (!cellShifts.length) return [];
      return cellShifts.map((s) => changeFor(row, date, s, fields));
    });
    if (items.length) onCommit(items);
  };

  const copyCell = () => {
    if (!focusKey) return;
    const { shifts: cellShifts } = cellsFor(focusKey);
    const s = cellShifts[0];
    if (!s) return;
    setClipboard({
      employee_id: s.employee_id, is_open_post: Boolean(s.is_open_post),
      open_post_reason: s.open_post_reason || undefined,
      time_in: hhmm(s.time_in), time_out: hhmm(s.time_out), next_day: Boolean(s.next_day),
      hours_type_id: s.hours_type_id, non_billable: Boolean(s.non_billable),
      rollover_type: s.rollover_type || undefined,
    });
  };

  const pasteCells = () => {
    if (!clipboard) return;
    const keys = selection.length ? selection : focusKey ? [focusKey] : [];
    const items = keys.map(cellsFor).filter((t) => t.row)
      .map(({ row, date, shifts: cellShifts }) => changeFor(row, date, cellShifts[0], clipboard));
    if (items.length) onCommit(items);
  };

  const duplicateCell = () => {
    if (!focusKey) return;
    const { row, date, shifts: cellShifts } = cellsFor(focusKey);
    const s = cellShifts[0];
    if (!s) return;
    onCommit([{
      key: `dup:${focusKey}:${Date.now()}`,
      change: {
        op: 'upsert', id: null,
        fields: {
          ...scaffold(row, date),
          time_in: hhmm(s.time_in), time_out: hhmm(s.time_out), next_day: Boolean(s.next_day),
          hours_type_id: s.hours_type_id, billing_item_id: s.billing_item_id,
          is_open_post: true, open_post_reason: 'Second officer on this window',
        },
      },
    }]);
  };

  const removeSelection = () => {
    const keys = selection.length ? selection : focusKey ? [focusKey] : [];
    const items = keys.map(cellsFor).flatMap(({ shifts: cellShifts }) =>
      cellShifts.map((s) => ({ key: `del:${s.id}`, change: { op: 'delete', id: s.id } })));
    if (items.length) onCommit(items);
    setSelection([]);
  };

  // ── keyboard: arrows move, Enter picks, shortcuts act ────────────────────
  const visibleCells = useMemo(() => {
    const out = [];
    groups.forEach((g) => {
      if (collapsed[g.key]) return;
      g.rows.filter(rowMatches).forEach((row) => {
        shownDates.forEach((d) => out.push(`${row.key}||${d}`));
      });
    });
    return out;
  }, [groups, collapsed, rowMatches, shownDates]);

  const move = (delta) => {
    if (!visibleCells.length) return;
    const i = visibleCells.indexOf(focusKey);
    const next = visibleCells[Math.min(Math.max((i === -1 ? 0 : i) + delta, 0), visibleCells.length - 1)];
    setFocusKey(next);
    cellRefs.current.get(next)?.focus();
  };

  const onKeyDown = (e) => {
    const cols = shownDates.length;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === 'c') { e.preventDefault(); copyCell(); return; }
    if (mod && e.key.toLowerCase() === 'v') { e.preventDefault(); pasteCells(); return; }
    if (mod && e.key.toLowerCase() === 'd') { e.preventDefault(); duplicateCell(); return; }
    switch (e.key) {
      case 'ArrowRight': e.preventDefault(); move(1); break;
      case 'ArrowLeft': e.preventDefault(); move(-1); break;
      case 'ArrowDown': e.preventDefault(); move(cols); break;
      case 'ArrowUp': e.preventDefault(); move(-cols); break;
      case 'Enter': {
        e.preventDefault();
        if (!focusKey) return;
        const { row, date } = cellsFor(focusKey);
        if (row) openPicker(row, date, cellRefs.current.get(focusKey));
        break;
      }
      case ' ': {
        e.preventDefault();
        if (focusKey) {
          setSelection((prev) => (prev.includes(focusKey)
            ? prev.filter((k) => k !== focusKey) : [...prev, focusKey]));
        }
        break;
      }
      case 'Delete': case 'Backspace': e.preventDefault(); removeSelection(); break;
      case 'Escape': setSelection([]); break;
      default: break;
    }
  };

  useEffect(() => { if (!focusKey && visibleCells.length) setFocusKey(visibleCells[0]); },
    [visibleCells, focusKey]);

  const selectionCount = selection.length || (focusKey ? 1 : 0);
  const blockedItem = blocked?.[0] || null;

  /** One acknowledgement per action, not per cell.
   *
   *  A fill-row, a paste across a range or a multi-select change can put
   *  several officers over forty at once, and the server returns one blocked
   *  item per CELL. Asking the same question seven times is noise, and the old
   *  "all 7" button was worse than noise: it showed one cell's projected hours
   *  while acknowledging seven. So the items are folded to one row per officer,
   *  carrying that officer's HIGHEST projected figure - the server accumulates
   *  hours within the batch, so the last cell for an officer is their real
   *  week - and one confirmation answers for all of them. */
  const overtimeGroup = useMemo(() => {
    const items = (blocked || []).filter(
      (b) => b.code === 'overtime_acknowledgement_required');
    const byOfficer = new Map();
    items.forEach((b) => {
      const prev = byOfficer.get(b.employee_id);
      if (!prev || (b.projected_week_hours || 0) > prev.projected_week_hours) {
        byOfficer.set(b.employee_id, {
          employee_id: b.employee_id,
          projected_week_hours: b.projected_week_hours,
          threshold_hours: b.threshold_hours,
          hours_over: b.hours_over,
        });
      }
    });
    return { officers: Array.from(byOfficer.values()), shiftCount: items.length };
  }, [blocked]);

  const unfilledIn = (rowsList) => rowsList.reduce((n, r) =>
    n + Object.values(r.cells).flat().filter((s) => s.is_open_post || !s.employee_id).length, 0);
  const hoursIn = (rowsList) => Math.round(rowsList.reduce((n, r) =>
    n + Object.values(r.cells).flat().reduce((h, s) => h + (Number(s.hours_calculated) || 0), 0), 0) * 100) / 100;

  return (
    <div className="space-y-3" data-testid="week-grid">
      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-line bg-surface-sunken p-1">
          {[['none', 'All'], ['unfilled', 'Unfilled'], ['overtime', 'Over 40'], ['armed', 'Armed']]
            .map(([v, label]) => (
              <button key={v} type="button" onClick={() => setFilter(v)}
                data-testid={`grid-filter-${v}`}
                className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                  filter === v ? 'bg-surface text-fg shadow-sm' : 'text-fg-muted hover:text-fg'}`}>
                {label}
              </button>
            ))}
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-line bg-surface-sunken p-1">
          <button type="button" onClick={() => setDayFocus('')} data-testid="grid-day-all"
            className={`text-xs px-2 py-1 rounded-md ${!dayFocus ? 'bg-surface text-fg shadow-sm' : 'text-fg-muted hover:text-fg'}`}>
            Week
          </button>
          {dates.map((d, i) => (
            <button key={d} type="button" onClick={() => setDayFocus(d)}
              data-testid={`grid-day-${d}`}
              className={`text-xs px-2 py-1 rounded-md ${dayFocus === d ? 'bg-surface text-fg shadow-sm' : 'text-fg-muted hover:text-fg'}`}>
              {DAY_LABELS[i]}
            </button>
          ))}
        </div>

        {canEdit && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-fg-muted" data-testid="grid-selection-count">
              {selectionCount} selected
            </span>
            <Button variant="outline" size="sm" onClick={() => setAvailOpen(true)}
              data-testid="grid-available-hours">
              <Clock className="h-3.5 w-3.5 mr-1.5" /> Who has hours left
            </Button>
            <Button variant="outline" size="sm" onClick={copyCell} data-testid="grid-copy">
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
            </Button>
            <Button variant="outline" size="sm" onClick={pasteCells} disabled={!clipboard}
              data-testid="grid-paste">
              <Clipboard className="h-3.5 w-3.5 mr-1.5" /> Paste
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="grid-bulk-menu">
                  <Users className="h-3.5 w-3.5 mr-1.5" /> {selectionCount} shift actions
                  <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setDialog({ kind: 'hours' })}
                  data-testid="grid-action-hours-type">
                  <Clock className="h-3.5 w-3.5 mr-2" /> Hours type or billable…
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDialog({ kind: 'times' })}
                  data-testid="grid-action-times">
                  <Clock className="h-3.5 w-3.5 mr-2" /> Change times…
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDialog({ kind: 'move' })}
                  data-testid="grid-action-move">
                  <MoveRight className="h-3.5 w-3.5 mr-2" /> Move to…
                </DropdownMenuItem>
                <DropdownMenuItem onClick={duplicateCell} data-testid="grid-action-duplicate">
                  <UserPlus className="h-3.5 w-3.5 mr-2" /> Duplicate for a second officer
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={removeSelection} data-testid="grid-action-delete">
                  <Trash2 className="h-3.5 w-3.5 mr-2" /> Remove shift
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* grid */}
      <div ref={gridRef} onKeyDown={onKeyDown}
        className="overflow-auto rounded-xl border border-line bg-surface max-h-[70vh]">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-20 bg-surface-sunken">
            <tr>
              <th className="sticky left-0 z-30 bg-surface-sunken text-left px-3 py-2
                             border-b border-r border-line min-w-[11rem] max-w-[14rem]">
                <span className="text-xs uppercase tracking-wide text-fg-muted">Post</span>
              </th>
              {shownDates.map((d) => (
                <th key={d} className="px-2 py-2 border-b border-r border-line last:border-r-0
                                       text-left min-w-[7.5rem]">
                  <span className="text-xs font-medium text-fg">
                    {DAY_LABELS[dates.indexOf(d)]}
                  </span>
                  <span className="block text-[11px] text-fg-muted tabular-nums">{d.slice(5)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const groupRows = g.rows.filter(rowMatches);
              if (!groupRows.length) return null;
              const isCollapsed = collapsed[g.key];
              return (
                <React.Fragment key={g.key}>
                  {groups.length > 1 && (
                    <tr className="bg-surface-sunken/60">
                      <td colSpan={shownDates.length + 1} className="px-3 py-1.5 border-b border-line">
                        <button type="button" data-testid={`grid-group-${g.key}`}
                          onClick={() => setCollapsed((p) => ({ ...p, [g.key]: !p[g.key] }))}
                          className="flex items-center gap-1.5 text-xs text-fg">
                          {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" />
                            : <ChevronDown className="h-3.5 w-3.5" />}
                          <span className="font-medium">{g.label}</span>
                          <span className="text-fg-muted tabular-nums">
                            {groupRows.length} rows · {hoursIn(groupRows)}h
                            {unfilledIn(groupRows) ? ` · ${unfilledIn(groupRows)} unfilled` : ''}
                          </span>
                        </button>
                      </td>
                    </tr>
                  )}
                  {!isCollapsed && groupRows.map((row) => (
                    <tr key={row.key} data-testid={`grid-row-${row.key}`}>
                      <th scope="row"
                        className="sticky left-0 z-10 bg-surface text-left px-3 py-1.5
                                   border-b border-r border-line align-top
                                   min-w-[11rem] max-w-[14rem]">
                        <span className="block text-xs font-medium text-fg truncate"
                          title={row.post?.post_name}>
                          {row.post?.post_name || 'Post'}
                        </span>
                        <span className="block text-[11px] text-fg-muted tabular-nums">
                          {row.timeIn ? `${row.timeIn}–${row.timeOut}` : 'no shifts yet'}
                          {row.nextDay ? ' (+1d)' : ''}
                        </span>
                        {canEdit && (
                          <div className="flex items-center gap-1 mt-1">
                            <button type="button" data-testid={`grid-fill-row-${row.key}`}
                              onClick={(e) => openPicker(row, dates.find((d) => (row.cells[d] || []).length) || dates[0], e.currentTarget, true)}
                              className="text-[10px] px-1.5 py-0.5 rounded-full border border-line
                                         text-fg-muted hover:text-fg hover:border-primary">
                              Fill row
                            </button>
                            <button type="button" data-testid={`grid-row-times-${row.key}`}
                              onClick={() => setDialog({ kind: 'rowTimes', row })}
                              className="text-[10px] px-1.5 py-0.5 rounded-full border border-line
                                         text-fg-muted hover:text-fg hover:border-primary">
                              Times
                            </button>
                            <button type="button" data-testid={`grid-row-add-${row.key}`}
                              onClick={() => setDialog({ kind: 'newRow', row })}
                              aria-label="Add a shift on this post"
                              className="text-[10px] px-1.5 py-0.5 rounded-full border border-line
                                         text-fg-muted hover:text-fg hover:border-primary">
                              <CalendarPlus className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </th>
                      {shownDates.map((d) => {
                        const key = `${row.key}||${d}`;
                        return (
                          <Cell key={key} cellKey={key} shifts={row.cells[d] || []}
                            names={names} showPay={showPay} showBill={showBill} canEdit={canEdit}
                            selected={selection.includes(key)} focused={focusKey === key}
                            cellRef={(el) => { if (el) cellRefs.current.set(key, el); }}
                            onOpen={() => { setFocusKey(key); openPicker(row, d, cellRefs.current.get(key)); }}
                            onSelect={() => {
                              setFocusKey(key);
                              setSelection((prev) => (prev.includes(key)
                                ? prev.filter((k) => k !== key) : [...prev, key]));
                            }}
                            onMenu={(e) => {
                              const a = e?.currentTarget?.getBoundingClientRect();
                              setFocusKey(key); setSelection([key]);
                              setDialog({ kind: 'cell', key, anchor: a
                                ? { top: a.top, left: a.left, width: a.width, height: a.height } : null });
                            }}
                            onHoliday={onHoliday} />
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* officer totals, hours only */}
      {totals?.officers?.length ? (
        <div className="flex flex-wrap gap-2" data-testid="grid-officer-totals">
          {totals.officers.map((o) => (
            <button key={o.employee_id} type="button"
              onClick={() => onOpenOfficer(o.employee_id)}
              data-testid={`officer-total-${o.employee_id}`}
              className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors
                          ${o.over_threshold ? 'border-warning-border text-warning bg-warning-bg/30'
                            : 'border-line text-fg-muted hover:text-fg'}`}>
              {names.get(o.employee_id) || 'Officer'} · {o.hours_this_week_all_jobs}h
              {o.over_threshold ? ' over 40' : ` · ${o.hours_remaining}h left`}
            </button>
          ))}
        </div>
      ) : null}

      {/* one picker, anchored */}
      {picker && (
        <div className="fixed z-50" style={picker.anchor || { top: 100, left: 100 }}>
          <OfficerPicker cell={picker.cell} jobId={job?.id} onChoose={onPick}
            onOpenPost={() => { setDialog({ kind: 'openPost', cell: picker.cell }); setPicker(null); }}
            onClear={() => {
              const s = (rows.find((r) => r.key === picker.cell.rowKey)?.cells[picker.cell.date] || [])[0];
              if (s) onCommit([{ key: `del:${s.id}`, change: { op: 'delete', id: s.id } }]);
              setPicker(null);
            }}
            onClose={() => setPicker(null)} />
        </div>
      )}

      {/* the questions */}
      <AvailableHoursSheet open={availOpen} onOpenChange={setAvailOpen}
        weekStart={weekStart} jobId={job?.id} cell={availOpen ? focusedCell() : null}
        onChoose={(officer) => {
          const cell = focusedCell();
          setAvailOpen(false);
          if (!cell) return;
          const row = rows.find((r) => r.key === cell.rowKey);
          chooseFor(officer, [{ row, date: cell.date, shifts: row?.cells[cell.date] || [] }]);
        }} />

      {localBlock && (
        <ShiftBlockedDialog item={{ ...localBlock, key: 'local' }} remaining={0}
          reasons={callOffReasons}
          onResolve={() => { localBlock.apply(); setLocalBlock(null); }}
          onResolveAll={() => { localBlock.apply(); setLocalBlock(null); }}
          onCancel={() => setLocalBlock(null)} />
      )}
      {!localBlock && blockedItem && (
        <ShiftBlockedDialog item={blockedItem} remaining={(blocked?.length || 1) - 1}
          reasons={callOffReasons} names={names} overtimeGroup={overtimeGroup}
          onResolve={(answer) => onResolveBlocked(blockedItem, answer, false)}
          onResolveAll={(answer) => onResolveBlocked(blockedItem, answer, true)}
          onCancel={onCancelBlocked} />
      )}

      <TimesDialog open={dialog?.kind === 'times' || dialog?.kind === 'rowTimes' || dialog?.kind === 'newRow'}
        onOpenChange={(v) => { if (!v) setDialog(null); }}
        initial={dialog?.kind === 'rowTimes' || dialog?.kind === 'newRow'
          ? { time_in: dialog.row?.timeIn, time_out: dialog.row?.timeOut, next_day: dialog.row?.nextDay }
          : (() => { const c = focusKey ? cellsFor(focusKey).shifts[0] : null; return c || null; })()}
        scopeLabel={dialog?.kind === 'rowTimes' ? 'Every shift on this row'
          : dialog?.kind === 'newRow' ? 'A new shift on this post'
          : `${selectionCount} shift${selectionCount === 1 ? '' : 's'}`}
        onApply={(fields) => {
          if (dialog?.kind === 'rowTimes') {
            const row = dialog.row;
            const items = dates.filter((d) => (row.cells[d] || []).length)
              .flatMap((d) => row.cells[d].map((s) => changeFor(row, d, s, fields)));
            if (items.length) onCommit(items);
          } else if (dialog?.kind === 'newRow') {
            const row = dialog.row;
            const day = dayFocus || dates[0];
            onCommit([{
              key: `new:${row.key}|${day}|${Date.now()}`,
              change: {
                op: 'upsert', id: null,
                fields: {
                  ...scaffold(row, day), ...fields,
                  is_open_post: true, open_post_reason: 'New shift, no officer yet',
                },
              },
            }]);
          } else {
            applyToSelection(fields);
          }
          setDialog(null);
        }} />

      <MoveDialog open={dialog?.kind === 'move'} onOpenChange={(v) => { if (!v) setDialog(null); }}
        posts={posts || []} dates={dates} count={selectionCount}
        onApply={(fields) => { applyToSelection(fields); setDialog(null); }} />

      <HoursTypeDialog open={dialog?.kind === 'hours'} onOpenChange={(v) => { if (!v) setDialog(null); }}
        hoursTypes={hoursTypes || []} count={selectionCount}
        onApply={(fields) => { applyToSelection(fields); setDialog(null); }} />

      <OpenPostDialog open={dialog?.kind === 'openPost'} onOpenChange={(v) => { if (!v) setDialog(null); }}
        onApply={(reason) => {
          const cell = dialog.cell;
          const row = rows.find((r) => r.key === cell.rowKey);
          const s = (row?.cells[cell.date] || [])[0];
          onCommit([changeFor(row, cell.date, s,
            { is_open_post: true, open_post_reason: reason, employee_id: null })]);
          setDialog(null);
        }} />

      {dialog?.kind === 'cell' && (
        <DropdownMenu open onOpenChange={(v) => { if (!v) setDialog(null); }}>
          <DropdownMenuTrigger asChild>
            <span aria-hidden className="pointer-events-none"
              style={dialog.anchor
                ? { position: 'fixed', top: dialog.anchor.top, left: dialog.anchor.left,
                    width: dialog.anchor.width, height: dialog.anchor.height }
                : { position: 'fixed', top: 100, left: 100 }} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={4} collisionPadding={8}
            data-testid="cell-actions-menu">
            <DropdownMenuItem onClick={() => setDialog({ kind: 'times' })}>
              Change times…
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDialog({ kind: 'hours' })}>
              Hours type or billable…
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDialog({ kind: 'move' })}>Move to…</DropdownMenuItem>
            <DropdownMenuItem onClick={() => { duplicateCell(); setDialog(null); }}>
              Duplicate for a second officer
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { copyCell(); setDialog(null); }}>Copy</DropdownMenuItem>
            <DropdownMenuItem onClick={() => { pasteCells(); setDialog(null); }} disabled={!clipboard}>
              Paste
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { removeSelection(); setDialog(null); }}>
              Remove shift
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <p className="text-[11px] text-fg-muted" data-testid="grid-keyboard-hint">
        Arrow keys move · Enter picks an officer · Space selects · ⌘/Ctrl-C and ⌘/Ctrl-V copy and
        paste across a range · ⌘/Ctrl-D duplicates · Delete removes. Every one of those is in the
        menus too.
      </p>
    </div>
  );
}
